import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import type { AuditEntry, AuditList } from '@mnc/contracts';
import { api, qs } from '../../lib/api';
import { DataTable } from '../../components/DataTable';
import { Badge, Modal, PageHeader } from '../../components/ui';

// Keep in sync with AuditAction in @mnc/contracts (checked by apps/api/test/consistency.test.ts).
const ACTIONS = ['create', 'update', 'delete', 'login', 'login_failed', 'logout', 'permission_change', 'config_change', 'lockout', 'restricted_access', 'export', 'import', 'sso_issue', 'sso_login', 'sso_revoke', 'sso_sync', 'totp_change', 'totp_recovery_used'];

export function AuditPage() {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState({ page: 1, page_size: 25 });
  const [sort, setSort] = useState('');
  const [action, setAction] = useState('');
  const [entity, setEntity] = useState('');
  const [detail, setDetail] = useState<AuditEntry | null>(null);
  const list = useQuery({ queryKey: ['audit', { ...page, sort, action, entity }], queryFn: () => api.get<AuditList>(`/api/audit${qs({ ...page, sort, action, entity_type: entity })}`) });

  const columns = useMemo<ColumnDef<AuditEntry, unknown>[]>(() => [
    { id: 'created_at', header: t('audit.when'), accessorKey: 'created_at', cell: ({ row }) => <span className="whitespace-nowrap text-xs">{new Date(row.original.created_at).toLocaleString(i18n.language)}</span> },
    { id: 'actor', header: t('audit.actor'), enableSorting: false, accessorFn: (r) => r.actor_display_name ?? '', cell: ({ row }) => row.original.actor_display_name ?? <span className="text-neutral-400">{t('audit.system')}</span> },
    { id: 'action', header: t('audit.action'), accessorKey: 'action', cell: ({ row }) => <Badge tone={row.original.action.includes('fail') || row.original.action === 'lockout' || row.original.action === 'delete' ? 'danger' : row.original.action.includes('change') || row.original.action === 'restricted_access' ? 'warn' : 'neutral'}>{row.original.action}</Badge> },
    { id: 'entity_type', header: t('audit.entity'), accessorFn: (r) => `${r.entity_type} ${r.entity_id ?? ''}`, cell: ({ row }) => <span className="text-xs">{row.original.entity_type} <span className="text-neutral-400">{row.original.entity_id}</span></span> },
    { id: 'ip', header: 'IP', enableSorting: false, accessorKey: 'ip', cell: ({ row }) => <span className="text-xs text-neutral-500">{row.original.ip}</span> },
  ], [t, i18n.language]);

  return (
    <div>
      <PageHeader title={t('audit.title')} />
      <DataTable
        columns={columns}
        data={list.data?.items ?? []}
        loading={list.isLoading}
        onRowClick={setDetail}
        sort={{ value: sort || (list.data?.meta.sort ?? ''), onChange: (s) => (setSort(s), setPage((p) => ({ ...p, page: 1 }))) }}
        page={list.data ? { ...list.data.meta, onChange: (p, s) => setPage({ page: p, page_size: s }) } : undefined}
        toolbar={
          <>
            <select className="input w-auto" value={action} onChange={(e) => (setAction(e.target.value), setPage((p) => ({ ...p, page: 1 })))}>
              <option value="">{t('audit.filterAction')}</option>
              {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <input className="input w-48" placeholder={t('audit.filterEntity')} value={entity} onChange={(e) => (setEntity(e.target.value), setPage((p) => ({ ...p, page: 1 })))} />
          </>
        }
      />
      <Modal open={detail !== null} onClose={() => setDetail(null)} title={t('audit.details')} wide>
        {detail && (
          <div className="grid gap-3 text-sm md:grid-cols-2">
            <div><div className="label">before</div><pre className="max-h-80 overflow-auto rounded-lg bg-neutral-100 p-2 text-xs dark:bg-neutral-800" dir="ltr">{JSON.stringify(detail.before, null, 2) ?? '—'}</pre></div>
            <div><div className="label">after</div><pre className="max-h-80 overflow-auto rounded-lg bg-neutral-100 p-2 text-xs dark:bg-neutral-800" dir="ltr">{JSON.stringify(detail.after, null, 2) ?? '—'}</pre></div>
            <div className="text-xs text-neutral-500 md:col-span-2">{detail.user_agent}</div>
          </div>
        )}
      </Modal>
    </div>
  );
}
